import { t } from 'translation'

import {
  SET_MISSING_SONG_IMAGE_INFO,
  DOWNLOAD_SONGS_START,
  DOWNLOAD_SONGS_FAIL,
  DOWNLOAD_SONG_SUCCESS,
  DOWNLOAD_SONGS_SUCCESS,
  DOWNLOAD_SONG_REMOVE,
  SET_DOWNLOAD_ID,
  SET_DOWNLOAD_PROGRESS,
  SET_DOWNLOAD_NUMBER,
  TOGGLE_DOWNLOAD_IN_BACKGROUND,
  SET_DOWNLOAD_PROMISE,
  INITIALIZE_DOWNLOADED_SONGS,
  SET_MISSING_SONG_INFO,
  SET_MISSING_GENRE_IMAGE_INFO,
  DOWNLOAD_COMPANY_LOGO_SUCCESS,
  DOWNLOAD_COMPANY_LOGO_FAIL,
} from './actionTypes'

import { extractStringFragment } from 'utils/conversion'
import {
  createOrAccessExistingDirectory,
  startFileDownload,
  getFileDestination,
  getDirectoryDestination,
  moveFromTempToDownloaded,
  removeFile,
  encryptAudioFile,
  removeTmpFile,
  moveFromTempCompanyLogoToDownloaded,
  removeTmpCompanyLogoFile,
} from 'utils/cordovaFs'

import {
  getSongInfoFromStore,
  saveSongInfoToStore,
  deleteSongInfoFromStore,
} from 'utils/storage'

import { fetchAllLocalMusic } from './playerActions'

import { keyBy } from 'lodash'

import { getNativeLocalPath } from 'utils/conversion'

import GenericSongImg from 'assets/covers_img/cover_no-connection.jpg'

export const toggleDownloadInBackground = () => (dispatch) =>
  dispatch({ type: TOGGLE_DOWNLOAD_IN_BACKGROUND })

export const setDownloadPromise = (promise) => (dispatch) =>
  dispatch({ type: SET_DOWNLOAD_PROMISE, payload: promise })

export const downloadMissingGenreImages =
  (genresWithMissingImages) => async (dispatch) => {
    if (!genresWithMissingImages.length) return

    const completed = []
    try {
      const tempSongsDirectory = await getDirectoryDestination(
        window.PERSISTENT,
        'tempSongs',
        { create: true }
      )

      for (const genre of genresWithMissingImages) {
        const { id, imageUrl } = genre

        const remoteUrlImage = imageUrl
        const imgExtension = remoteUrlImage.match(/[^.]+$/)[0]

        const tempImageDestination = await getFileDestination(
          `genre_${id}.${imgExtension}`,
          tempSongsDirectory,
          {
            create: true,
          }
        )

        let errorOnDownload = false
        let downloadRetryCount = 0

        do {
          try {
            await startFileDownload(tempImageDestination, remoteUrlImage)
            errorOnDownload = false
          } catch (error) {
            await new Promise((res) => setTimeout(res, 250))

            errorOnDownload = true
            downloadRetryCount += 1
            console.log('Error downloading genre image:', error)
          }
        } while (errorOnDownload && downloadRetryCount < 5)

        await moveFromTempToDownloaded(`genre_${id}.${imgExtension}`, tempSongsDirectory)

        dispatch({
          type: SET_MISSING_GENRE_IMAGE_INFO,
          payload: { id, ext: imgExtension },
        })
        completed.push(id)
      }
    } catch (error) {
      console.log(error)
    } finally {
      return completed
    }
  }

export const downloadMissingSongImages =
  (songIdsOfImages, allRawSongsMap) => async (dispatch) => {
    if (!songIdsOfImages.length) return
    const completed = []
    try {
      const tempSongsDirectory = await getDirectoryDestination(
        window.PERSISTENT,
        'tempSongs',
        { create: true }
      )

      for (const songId of songIdsOfImages) {
        if (!allRawSongsMap[songId]) continue

        const remoteUrlImage = allRawSongsMap[songId].imageUrl
        const imgExtension = remoteUrlImage.match(/[^.]+$/)[0]

        const tempImageDestination = await getFileDestination(
          `image_${songId}.${imgExtension}`,
          tempSongsDirectory,
          {
            create: true,
          }
        )

        let errorOnDownload = false
        let downloadRetryCount = 0

        do {
          try {
            await startFileDownload(tempImageDestination, remoteUrlImage)
            errorOnDownload = false
          } catch (error) {
            await new Promise((res) => setTimeout(res, 250))

            errorOnDownload = true
            downloadRetryCount += 1
            console.log('Error downloading image:', error)
          }
        } while (errorOnDownload && downloadRetryCount < 5)

        const songInfoRaw = { ...allRawSongsMap[songId] }
        songInfoRaw.imageUrl = await getNativeLocalPath(
          `songs/image_${songId}.${imgExtension}`
        )

        await moveFromTempToDownloaded(
          `image_${songId}.${imgExtension}`,
          tempSongsDirectory
        )

        dispatch({
          type: SET_MISSING_SONG_IMAGE_INFO,
          payload: { songInfoRaw, idExt: { id: songId, ext: imgExtension } },
        })
        completed.push(songId)
      }
    } catch (error) {
      console.log(error)
    } finally {
      return completed
    }
  }

export const downloadSongs =
  (songIdsToDownload, songsMap) => async (dispatch, getState) => {
    const downloadNumberTotal = songIdsToDownload.length
    const setDownloadProgress = (progress) =>
      dispatch({ type: SET_DOWNLOAD_PROGRESS, payload: progress })

    let count = 1
    try {
      const tempSongsDirectory = await getDirectoryDestination(
        window.PERSISTENT,
        'tempSongs',
        { create: true }
      )

      const { allRawSongsMap } = getState().player

      for (const songId of songIdsToDownload) {
        dispatch({ type: SET_DOWNLOAD_NUMBER, payload: count })
        dispatch({ type: SET_DOWNLOAD_ID, payload: songId })
        setDownloadProgress(0)

        // Since the 3 download properties above are now set,
        // open up the download modal
        dispatch({ type: DOWNLOAD_SONGS_START, payload: downloadNumberTotal })

        const remoteUrlImage = songsMap[songId].imageUrl
        const remoteUrlAudio = songsMap[songId].audioUrl

        const imgExtension = remoteUrlImage.match(/[^.]+$/)[0]

        const isIos = window.cordova && window.device?.platform === 'iOS'

        const tempSongDestination = await getFileDestination(
          getState().auth.isPro || isIos ? `song_${songId}.mp3` : `song_${songId}`,
          tempSongsDirectory,
          {
            create: true,
          }
        )

        const tempImageDestination = await getFileDestination(
          `image_${songId}.${imgExtension}`,
          tempSongsDirectory,
          {
            create: true,
          }
        )

        let errorOnDownload = false
        let downloadRetryCount = 0

        do {
          try {
            const imageDownloadPromise = startFileDownload(
              tempImageDestination,
              remoteUrlImage
            )

            await startFileDownload(
              tempSongDestination,
              remoteUrlAudio,
              setDownloadProgress
            )

            await imageDownloadPromise

            errorOnDownload = false
          } catch (error) {
            if (downloadRetryCount > 10) throw Error('Error downloading files')

            await new Promise((res) => setTimeout(res, 250))

            errorOnDownload = true
            downloadRetryCount += 1
            console.log('Error downloading file:', error)
          }
        } while (errorOnDownload)

        if (!getState().auth.isPro && !isIos) {
          await encryptAudioFile(
            tempSongDestination.nativeURL,
            `${tempSongDestination.nativeURL}.mc`
          )
          await removeTmpFile(`song_${songId}`)
        }
        const songInfoRaw = allRawSongsMap[songId]
        await saveSongInfoToStore(songInfoRaw)
        await moveFromTempToDownloaded(
          getState().auth.isPro || isIos ? `song_${songId}.mp3` : `song_${songId}.mc`,
          tempSongsDirectory
        )
        await moveFromTempToDownloaded(
          `image_${songId}.${imgExtension}`,
          tempSongsDirectory
        )

        dispatch({
          type: DOWNLOAD_SONG_SUCCESS,
          payload: { songId, songInfoRaw },
        })

        count += 1
      }
      dispatch(fetchAllLocalMusic())
    } catch (error) {
      if (!getState().auth.isOnline) alert(t('settings.NOT_CONNECTED_TO_THE_INTERNET'))
      else alert(t('settings.UNABLE_TO_DOWNLOAD_AT_THIS_TIME'))

      console.log(error)

      return dispatch({ type: DOWNLOAD_SONGS_FAIL, payload: error })
    }

    // Wait 1 second before closing the modal after finishing download
    setTimeout(() => {
      dispatch({ type: DOWNLOAD_SONGS_SUCCESS })
    }, 1000)
  }

export const removeDownloadedSong = (songId) => async (dispatch, getState) => {
  try {
    const isIos = window.cordova && window.device?.platform === 'iOS'
    await removeFile(
      getState().auth.isPro || isIos ? `song_${songId}.mp3` : `song_${songId}.mc`
    )
    await deleteSongInfoFromStore(songId)

    const downloadedImages = getState().download.downloadedImages
    const downloadedImagesMap = keyBy(downloadedImages, 'id')
    if (downloadedImagesMap[songId])
      await removeFile(`image_${songId}.${downloadedImagesMap[songId].ext}`)

    dispatch(fetchAllLocalMusic())
    dispatch({ type: DOWNLOAD_SONG_REMOVE, payload: songId })
  } catch (stringErrCode) {
    alert(`settings.${stringErrCode}`)
  }
}

export const intializeDownloadedSongs = () => async (dispatch, getState) => {
  let getIdFromAudioFile
  const songsInit = JSON.parse(localStorage.getItem('songsInitialized'))

  if (songsInit)
    getIdFromAudioFile = (name) => extractStringFragment(name, /^song_(.*?)\.mc$/)
  else getIdFromAudioFile = (name) => extractStringFragment(name, /^song_(.*?)\.mp3$/)

  const getIdAndExtFromSongImageFile = (name) => {
    const res = name.match(/^image_(.*?)\.([^.]+)$/)
    return { id: res[1], ext: res[2] }
  }
  const getIdAndExtFromGenreImageFile = (name) => {
    const res = name.match(/^genre_(.*?)\.([^.]+)$/)
    return { id: res[1], ext: res[2] }
  }

  try {
    const fileEntries = await createOrAccessExistingDirectory(window.PERSISTENT, 'songs')

    const existingSongsFileIds = fileEntries
      .filter(({ name }) => name.startsWith('song'))
      .map(({ name }) => getIdFromAudioFile(name))

    const existingGenreImageFileIdsExts = fileEntries
      .filter(({ name }) => name.startsWith('genre'))
      .map(({ name }) => getIdAndExtFromGenreImageFile(name))

    const existingSongImageFileIdsExts = fileEntries
      .filter(({ name }) => name.startsWith('image'))
      .map(({ name }) => getIdAndExtFromSongImageFile(name))

    const existingSongImageFileIdsExtsMap = keyBy(existingSongImageFileIdsExts, 'id')

    const existingRawSongsInfo = await Promise.all(
      existingSongsFileIds.map((songId) => getSongInfoFromStore(songId))
    )

    const existingRawSongsInfoWithImages = await Promise.all(
      existingRawSongsInfo.map(async (loadedRawSong) => ({
        ...loadedRawSong,
        imageUrl: existingSongImageFileIdsExtsMap[loadedRawSong.id]
          ? await getNativeLocalPath(
              `songs/image_${loadedRawSong.id}.${
                existingSongImageFileIdsExtsMap[loadedRawSong.id].ext
              }`
            )
          : GenericSongImg,
      }))
    )

    const existingRawSongsInfoMap = keyBy(existingRawSongsInfo, 'id')

    const existingFilesAndInfo = existingSongsFileIds.filter(
      (songId) => existingRawSongsInfoMap[songId]
    )

    dispatch({
      type: INITIALIZE_DOWNLOADED_SONGS,
      payload: {
        // Files audio and infos exist
        downloadedSongs: existingFilesAndInfo,
        // Files audio which were found in memory
        downloadedFilesIds: existingSongsFileIds,
        // Infos exists which were found in memory
        downloadedRawSongsInfo: existingRawSongsInfoWithImages,
        // Image exists
        downloadedImages: existingSongImageFileIdsExts,
        downloadedGenreImages: existingGenreImageFileIdsExts,
      },
    })
  } catch (err) {
    console.log(err)
  }
}

export const addMissingOfflineSongInfo =
  (addedDownloadedSongs, addedDownloadedRawSongInfo) => (dispatch) => {
    dispatch({
      type: SET_MISSING_SONG_INFO,
      payload: { addedDownloadedSongs, addedDownloadedRawSongInfo },
    })
  }

export const downloadCompanyLogo =
  (companyLogoUrl, companyLogo) => async (dispatch, getState) => {
    let count = 1
    try {
      const tempCompanyLogoDirectory = await getDirectoryDestination(
        window.PERSISTENT,
        'tempCompanyLogo',
        { create: true }
      )

      const tempCompanyLogoDestination = await getFileDestination(
        companyLogo,
        tempCompanyLogoDirectory,
        {
          create: true,
        }
      )

      let errorOnDownload = false
      let downloadRetryCount = 0

      do {
        try {
          await startFileDownload(tempCompanyLogoDestination, companyLogoUrl)

          errorOnDownload = false
        } catch (error) {
          if (downloadRetryCount > 10) throw Error('Error downloading company logo')

          await new Promise((res) => setTimeout(res, 250))

          errorOnDownload = true
          downloadRetryCount += 1
          console.log('Error downloading file:', error)
        }
      } while (errorOnDownload)

      localStorage.setItem('companyLogoDownloaded', true)
      await moveFromTempCompanyLogoToDownloaded(companyLogo, tempCompanyLogoDirectory)
      await removeTmpCompanyLogoFile(companyLogo)

      dispatch({
        type: DOWNLOAD_COMPANY_LOGO_SUCCESS,
        payload: { companyLogo },
      })

      count += 1
    } catch (error) {
      if (!getState().auth.isOnline) alert(t('settings.NOT_CONNECTED_TO_THE_INTERNET'))
      else alert(t('settings.UNABLE_TO_DOWNLOAD_AT_THIS_TIME'))

      return dispatch({ type: DOWNLOAD_COMPANY_LOGO_FAIL, payload: error })
    }
  }
